import React, {useEffect, useMemo, useRef, useState} from "react";
import Button from "@rescui/button";
import {Input} from "@rescui/input";
import {Select} from "@rescui/select";
import {LayeringProvider, useLayeringContext} from "@rescui/ui-contexts";
import {CHANNELS} from "../../data/products";
import {getProduct} from "../../data/products/index";
import {RELEASES_SETS} from "../../data/release-sets";
import {type BannerSelection} from "../../data/template-availability";
import {availableTemplates, selectionKey} from "../catalog";
import {isPartnerLike} from "../../data/role-policy";

type Props = {channels: typeof CHANNELS; role: string; onClose: () => void; onAdd: (selections: BannerSelection[]) => Promise<void>};
export function TemplatePicker({channels, role, onClose, onAdd}: Props) {
  const dialog = useRef<HTMLDivElement>(null);
  const [channelId, setChannel] = useState(channels[0].value);
  const channel = channels.find((entry) => entry.value === channelId);
  if (!channel) throw new Error("Picker channel missing");
  const products = useMemo(() => [...new Set(channel.types.filter((t) => t.value !== "action").flatMap((t) => Object.keys(t.products)))]
    .map((code) => getProduct(code)).filter((p) => p && p.code !== "ALL" && (!isPartnerLike(role) || p.code === "JB")).sort((a, b) => a.label.localeCompare(b.label)), [channel, role]);
  const [productSlug, setProduct] = useState("");
  const product = products.find((p) => p.slug === productSlug) ?? products[0];
  if (!product) throw new Error("Picker has no products");
  const types = channel.types.filter((t) => t.value !== "action" && Object.hasOwn(t.products, product.code));
  const [typeId, setType] = useState("");
  const type = types.find((entry) => entry.value === typeId) ?? types[0];
  if (!type) throw new Error("Picker has no content types");
  const [setId, setSet] = useState("");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState<BannerSelection[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const results = availableTemplates({channel: channelId, product: product.slug, contentType: type.value});
  const sets = RELEASES_SETS.filter((s) => results.some((row) => row.selection.set === s.value));
  const visible = results.filter((row) => (!setId || row.selection.set === setId) && `${row.definition.label} ${row.definition.tooltip ?? ""}`.toLowerCase().includes(search.toLowerCase()));
  const selected = new Set(cart.map(selectionKey));
  const {popupZIndex} = useLayeringContext();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement;
    const main = document.querySelector(".grid-workspace main");
    if (!(main instanceof HTMLElement)) throw new Error("Grid workspace main is missing");
    main.inert = true;
    dialog.current.focus();
    return () => {main.inert = false; previous?.focus();};
  }, []);
  const add = async () => {
    setBusy(true); setError("");
    try {await onAdd(cart); onClose();} catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not add banners"); setBusy(false);
    }
  };
  return <LayeringProvider baseLayer="popup"><div className="grid-picker-backdrop" style={{zIndex: popupZIndex}}>
    <div ref={dialog} className="grid-picker" role="dialog" aria-modal="true" tabIndex={-1} aria-labelledby="grid-picker-title"
      onKeyDown={(e) => {
        if (e.defaultPrevented) return;
        if (e.key === "Escape") {e.preventDefault(); if (!busy) onClose();}
        if (e.key === "Tab") {
          const elements = Array.from<HTMLElement>(dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
          const first = elements[0], last = elements[elements.length - 1];
          if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {e.preventDefault(); last?.focus();}
          else if (!e.shiftKey && document.activeElement === last) {e.preventDefault(); first?.focus();}
        }
      }}>
    <div className="grid-picker-heading"><h2 id="grid-picker-title">Add banners to your grid</h2>
      <Button mode="clear" aria-label="Close template picker" disabled={busy} onClick={onClose}>×</Button></div>
    <fieldset disabled={busy} className="grid-picker-filters coverbakerForm">
      <div data-grid-filter="Channel"><Select label="Channel" options={channels} value={channel} isDisabled={busy} onChange={(value) => {setChannel(value.value); setProduct(""); setType(""); setSet("");}} size="m" /></div>
      <div data-grid-filter="Product"><Select label="Product" options={products.map((p) => ({label: p.label, value: p.slug}))} value={{label: product.label, value: product.slug}} isDisabled={busy} isSearchable onChange={(value) => {setProduct(value.value); setType(""); setSet("");}} size="m" /></div>
      <div data-grid-filter="Content type"><Select label={channelId === "releases" ? "Stage" : "Content type"} options={types} value={type} isDisabled={busy} onChange={(value) => {setType(value.value); setSet("");}} size="m" /></div>
      {sets.length > 0 && <div data-grid-filter="Set"><Select label="Set" options={[{label: "All sets", value: ""}, ...sets]} value={sets.find((set) => set.value === setId) ?? {label: "All sets", value: ""}} isDisabled={busy} onChange={(value) => setSet(value.value)} size="m" /></div>}
      <Input type="search" aria-label="Search templates" placeholder="Search templates" value={search} onChange={(e) => setSearch(e.target.value)} />
    </fieldset>
    <div className="grid-picker-results" aria-label="Templates">
      {visible.map(({definition, selection}) => {
        const checked = selected.has(selectionKey(selection));
        return <button key={definition.templateKey} className="grid-template-choice" aria-pressed={checked} disabled={busy}
          onClick={() => setCart((current) => checked ? current.filter((s) => selectionKey(s) !== selectionKey(selection)) : [...current, selection])}>
          <strong>{definition.label}</strong><small>{definition.width} × {definition.height} px{definition.set ? ` · ${RELEASES_SETS.find((s) => s.value === definition.set).label}` : ""}</small>
          <span aria-hidden="true">{checked ? "✓" : "+"}</span>
        </button>;
      })}
      {!visible.length && <p className="grid-muted">No templates match these filters.</p>}
    </div>
    <footer className="grid-picker-footer"><div aria-live="polite"><strong>{cart.length} selected</strong><span className="grid-muted"> across {new Set(cart.map((s) => s.product)).size} products</span>
      {cart.length > 0 && <Button mode="clear" size="s" disabled={busy} onClick={() => setCart([])}>Clear selection</Button>}</div>
      <Button disabled={busy || !cart.length} onClick={() => void add()}>{busy ? "Preparing banners…" : `Add ${cart.length || ""} ${cart.length === 1 ? "banner" : "banners"}`}</Button>
    </footer>
    {error && <p className="grid-error" role="alert">{error}. Your grid has not changed. Try adding again.</p>}
  </div></div></LayeringProvider>;
}
